import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import WorkFormPage from '../../../components/work/WorkFormPage';
import WorkManageEntityModal from '../../../components/work/WorkManageEntityModal';
import WorkSelectWithManage from '../../../components/work/WorkSelectWithManage';
import { contractsApi, contractTypesApi, contractTemplatesApi } from '../../../utils/workApi';
import { getContractTemplateFromResponse, getContractFromResponse } from '../../../utils/workResponseUtils';
import WorkCustomFields, {
  extractWorkCustomFieldsPayload,
  mapWorkCustomFieldsToForm,
} from '../../../components/work/WorkCustomFields';
import { clientsApi, show422Error } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';
import {
  mergeBreadcrumbIntoNavigationState,
  trailWithoutLastCrumb,
} from '../../../utils/breadcrumbTrail';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  message,
  Spin,
  Divider,
} from 'antd';
import WorkPriceInput from '../../../components/work/WorkPriceInput';
import { FileProtectOutlined, SaveOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import { parseContractTypesList } from '../../../utils/workFormUtils';
import { buildContractPayload } from '../../../utils/workPayloadBuilders';
import WorkCheckbox from '../../../components/work/WorkCheckbox';
import { toJalali } from '../../../utils/dateUtils';
import { stripHtml } from '../../../utils/stripHtml';

const { TextArea } = Input;
const { Option } = Select;

const mapClientToContractFields = (client) => {
  const d = client?.client_details || {};
  return {
    cell: client?.mobile || d.mobile || undefined,
    office: d.office || d.office_phone || undefined,
    city: d.city || undefined,
    state: d.state || undefined,
    postal_code: d.postal_code || undefined,
    alternate_address: d.address || undefined,
  };
};

const ContractForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const presetTemplateId = location.state?.templateId;
  const copyFromId = location.state?.copyFromId;
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/work/contracts', { state: buildGoBackState() });
  const { canAdd, canEdit } = useResourcePermissions('contracts');
  const [form] = Form.useForm();
  const subjectWatch = Form.useWatch('subject', form);
  const entityLabel = isEdit
    ? subjectWatch || `ویرایش قرارداد #${id}`
    : copyFromId
      ? 'کپی قرارداد'
      : 'قرارداد جدید';
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/contracts',
    listLabel: 'قراردادها',
    entityLabel,
  });
  const goToContractView = (contractId = id) => {
    if (!contractId) {
      goBackToList();
      return;
    }
    navigate(`/work/contracts/${contractId}`, {
      state: {
        returnTo: returnToRef.current || '/work/contracts',
        ...buildGoBackState(),
        ...mergeBreadcrumbIntoNavigationState({}, trailWithoutLastCrumb(trail)),
      },
    });
  };
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [contractTypes, setContractTypes] = useState([]);
  const [options, setOptions] = useState({ clients: [], currencies: [], projects: [] });
  const [templateOptions, setTemplateOptions] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [typeModal, setTypeModal] = useState(false);

  const refreshTypes = async () => {
    const res = await contractTypesApi.getCreateData();
    const types = parseContractTypesList(res);
    setContractTypes(types);
    return types;
  };

  const ensureContractTypeOption = (typeId, typeName) => {
    if (!typeId) return;
    setContractTypes((prev) => {
      if (prev.some((t) => Number(t.id) === Number(typeId))) return prev;
      return [...prev, { id: typeId, name: typeName || String(typeId) }];
    });
  };

  const applyTemplate = async (templateId, { silent = false } = {}) => {
    if (!templateId) return;
    setApplyingTemplate(true);
    try {
      const res = await contractTemplatesApi.getById(templateId);
      const tpl = getContractTemplateFromResponse(res);
      if (!tpl) return;
      const typeId = tpl.contract_type_id ?? tpl.contract_type?.id;
      if (typeId && tpl.contract_type?.name) {
        ensureContractTypeOption(typeId, tpl.contract_type.name);
      }
      form.setFieldsValue({
        subject: tpl.subject,
        description: tpl.description || tpl.contract_detail,
        contract_type: typeId != null ? Number(typeId) : undefined,
        currency_id: tpl.currency_id,
        amount: tpl.amount != null ? Number(String(tpl.amount).replace(/,/g, '')) : undefined,
      });
      if (!silent) {
        message.success('فیلدها از قالب پر شدند');
      }
    } catch {
      message.error('خطا در بارگذاری قالب');
    } finally {
      setApplyingTemplate(false);
    }
  };

  const fillFromClient = async (clientId) => {
    if (!clientId) {
      form.setFieldsValue({
        cell: undefined,
        office: undefined,
        city: undefined,
        state: undefined,
        postal_code: undefined,
        alternate_address: undefined,
      });
      return;
    }
    setLoadingClient(true);
    try {
      const fromList = (options.clients || []).find((c) => Number(c.id) === Number(clientId));
      const res = await clientsApi.getEditForm(clientId);
      const data = res?.data ?? res;
      const client = data?.client;
      if (client) {
        form.setFieldsValue(mapClientToContractFields(client));
      } else if (fromList) {
        form.setFieldsValue({
          cell: fromList.mobile || undefined,
        });
      }
    } catch {
      const fromList = (options.clients || []).find((c) => Number(c.id) === Number(clientId));
      if (fromList?.mobile) {
        form.setFieldsValue({ cell: fromList.mobile });
      }
    } finally {
      setLoadingClient(false);
    }
  };

  const applyCopyFromContract = async (sourceId) => {
    if (!sourceId) return;
    setApplyingTemplate(true);
    try {
      const res = await contractsApi.getById(sourceId);
      const c = getContractFromResponse(res);
      if (!c) return;
      const typeId = c.contract_type_id ?? c.contract_type?.id;
      if (typeId && c.contract_type?.name) {
        ensureContractTypeOption(typeId, c.contract_type.name);
      }
      form.setFieldsValue({
        subject: c.subject ? `${c.subject} (کپی)` : undefined,
        client_id: c.client_id,
        project_id: c.project_id,
        contract_type: typeId != null ? Number(typeId) : undefined,
        currency_id: c.currency_id,
        amount: c.original_amount || c.amount,
        start_date: c.original_start_date
          ? toJalali(c.original_start_date)
          : c.start_date
            ? toJalali(c.start_date)
            : undefined,
        end_date: c.original_end_date
          ? toJalali(c.original_end_date)
          : c.end_date
            ? toJalali(c.end_date)
            : undefined,
        without_end: !c.end_date && !c.original_end_date,
        description: c.description,
        cell: c.cell,
        office: c.office,
        city: c.city,
        state: c.state,
        postal_code: c.postal_code,
        alternate_address: c.alternate_address,
        note: c.contract_note || c.note,
      });
      message.success('اطلاعات قرارداد کپی شد');
    } catch {
      message.error('خطا در کپی قرارداد');
    } finally {
      setApplyingTemplate(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = isEdit ? await contractsApi.getEditData(id) : await contractsApi.getCreateData();
        let types = parseContractTypesList(res);
        if (!types.length) {
          try {
            types = await refreshTypes();
          } catch {
            types = [];
          }
        } else {
          setContractTypes(types);
        }
        setOptions({
          clients: res?.clients || [],
          currencies: res?.currencies || [],
          projects: res?.projects || [],
        });
        setCustomFieldDefs(Array.isArray(res?.custom_fields) ? res.custom_fields : []);
        if (!isEdit) {
          try {
            const tplRes = await contractTemplatesApi.getAll({ per_page: 100 });
            setTemplateOptions(tplRes?.data?.data || []);
          } catch {
            setTemplateOptions([]);
          }
        }
        if (isEdit && res?.contract) {
          const c = res.contract;
          const startRaw = c.original_start_date || c.start_date;
          const endRaw = c.without_end ? undefined : c.original_end_date || c.end_date;
          form.setFieldsValue({
            contract_number: c.original_contract_number || c.contract_number,
            subject: c.subject,
            client_id: c.client_id,
            project_id: c.project_id,
            contract_type: Number(c.contract_type_id || c.contract_type?.id) || undefined,
            currency_id: c.currency_id,
            amount: c.original_amount || c.amount,
            start_date: startRaw ? toJalali(startRaw) || startRaw : undefined,
            end_date: endRaw ? toJalali(endRaw) || endRaw : undefined,
            without_end: !c.end_date && !c.original_end_date,
            description: stripHtml(c.description || c.contract_detail),
            cell: c.cell,
            office: c.office,
            city: c.city,
            state: c.state,
            postal_code: c.postal_code,
            alternate_address: c.alternate_address,
            note: c.contract_note || c.note,
            ...mapWorkCustomFieldsToForm(res.custom_fields || [], c.custom_fields_data || c),
          });
        } else if (res?.next_contract_number) {
          form.setFieldsValue({
            contract_number: res.next_contract_number,
            ...mapWorkCustomFieldsToForm(res.custom_fields || []),
          });
        } else {
          form.setFieldsValue(mapWorkCustomFieldsToForm(res.custom_fields || []));
        }
        if (!isEdit && copyFromId) {
          await applyCopyFromContract(copyFromId);
        } else if (!isEdit && presetTemplateId) {
          await applyTemplate(presetTemplateId, { silent: true });
        }
      } catch {
        message.error('خطا در بارگذاری فرم');
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, form, presetTemplateId, copyFromId]);

  useEffect(() => {
    if (!fetching) {
      if (isEdit && !canEdit) navigate('/work/contracts', { replace: true });
      if (!isEdit && !canAdd) navigate('/work/contracts', { replace: true });
    }
  }, [fetching, isEdit, canAdd, canEdit, navigate]);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const body = {
        ...buildContractPayload(values),
        ...extractWorkCustomFieldsPayload(values, customFieldDefs),
      };
      if (isEdit) {
        await contractsApi.update(id, body);
        message.success('قرارداد به‌روز شد');
        goToContractView(id);
      } else {
        const res = await contractsApi.create(body);
        const newId = res?.contract?.id || res?.data?.contract?.id;
        message.success('قرارداد ایجاد شد');
        if (newId) goToContractView(newId);
        else goBackToList();
      }
    } catch (e) {
      show422Error(e, form);
    } finally {
      setSubmitting(false);
    }
  };

  const withoutEnd = Form.useWatch('without_end', form);

  return (
    <WorkFormPage
      title={isEdit ? 'ویرایش قرارداد' : copyFromId ? 'کپی قرارداد' : 'قرارداد جدید'}
      subtitle="اطلاعات قرارداد را تکمیل کنید"
      icon={FileProtectOutlined}
      iconClassName="bg-amber-50 dark:bg-amber-900/20"
      iconColorClass="text-amber-600 dark:text-amber-400"
      backPath={isEdit ? `/work/contracts/${id}` : '/work/contracts'}
      onBack={isEdit ? () => goToContractView(id) : goBackToList}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
    >
      {fetching ? (
        <FormSkeleton rows={10} columns={2} />
      ) : (
      <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
        <Spin
          spinning={applyingTemplate || loadingClient}
          tip={
            <span style={{ fontFamily: 'CustomFont' }}>
              {loadingClient ? 'در حال بارگذاری اطلاعات مشتری...' : 'در حال بارگذاری...'}
            </span>
          }
        >
        <Form
          form={form}
          layout="vertical"
          className="work-panel-form"
          style={{ fontFamily: 'CustomFont' }}
          onFinish={onFinish}
          onFinishFailed={() => scrollToFirstError(form)}
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        >
          {!isEdit && templateOptions.length > 0 && !copyFromId ? (
            <Row gutter={16} className="mb-2">
              <Col xs={24} sm={12}>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont' }}>قالب قرارداد (اختیاری)</span>}>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="انتخاب قالب برای پیش‌پر کردن"
                    disabled={applyingTemplate}
                    onChange={(tid) => applyTemplate(tid)}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {templateOptions.map((t) => (
                      <Option key={t.id} value={t.id} label={t.subject}>
                        {t.subject}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          ) : null}

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="contract_number" label={<span style={{ fontFamily: 'CustomFont' }}>شماره قرارداد</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} disabled={isEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="subject"
                label={<span style={{ fontFamily: 'CustomFont' }}>موضوع</span>}
                rules={[{ required: true }]}
              >
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="project_id" label={<span style={{ fontFamily: 'CustomFont' }}>پروژه</span>}>
                <Select allowClear style={{ fontFamily: 'CustomFont' }}>
                  {(options.projects || []).map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.project_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <WorkSelectWithManage
                name="contract_type"
                label="نوع قرارداد"
                required
                rules={[{ required: true, message: 'نوع قرارداد الزامی است' }]}
                onManageClick={() => setTypeModal(true)}
                manageTooltip="مدیریت انواع قرارداد"
                selectProps={{ showSearch: true, optionFilterProp: 'label' }}
              >
                {contractTypes.map((t) => (
                  <Option key={t.id} value={Number(t.id)} label={t.name}>
                    {t.name}
                  </Option>
                ))}
              </WorkSelectWithManage>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="currency_id"
                label={<span style={{ fontFamily: 'CustomFont' }}>ارز</span>}
                rules={[{ required: true }]}
              >
                <Select style={{ fontFamily: 'CustomFont' }}>
                  {(options.currencies || []).map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.currency_name} ({c.currency_symbol})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="amount"
                label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>}
                rules={[{ required: true }]}
              >
                <WorkPriceInput />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="start_date"
                label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>}
                rules={[{ required: true }]}
              >
                <PersianDatePicker className="w-full" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="end_date" label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ پایان</span>}>
                <PersianDatePicker className="w-full" disabled={withoutEnd} />
              </Form.Item>
              <Form.Item name="without_end" valuePropName="checked" className="mb-0">
                <WorkCheckbox>بدون تاریخ پایان</WorkCheckbox>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="description" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
                <TextArea rows={3} style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="right" style={{ fontFamily: 'CustomFont' }}>
            اطلاعات مشتری
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="client_id"
                label={<span style={{ fontFamily: 'CustomFont' }}>مشتری</span>}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ fontFamily: 'CustomFont' }}
                  onChange={(clientId) => {
                    fillFromClient(clientId);
                  }}
                  allowClear
                >
                  {(options.clients || []).map((c) => (
                    <Option key={c.id} value={c.id} label={c.name}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cell" label={<span style={{ fontFamily: 'CustomFont' }}>موبایل</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="office" label={<span style={{ fontFamily: 'CustomFont' }}>تلفن</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="postal_code" label={<span style={{ fontFamily: 'CustomFont' }}>کدپستی</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="city" label={<span style={{ fontFamily: 'CustomFont' }}>شهر</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="state" label={<span style={{ fontFamily: 'CustomFont' }}>استان</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="alternate_address" label={<span style={{ fontFamily: 'CustomFont' }}>آدرس</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          </Row>

          <WorkCustomFields fields={customFieldDefs} />

          <Form.Item name="note" label={<span style={{ fontFamily: 'CustomFont' }}>یادداشت</span>}>
            <TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              onClick={() => (isEdit ? goToContractView(id) : goBackToList())}
              style={{ fontFamily: 'CustomFont' }}
            >
              انصراف
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
              className={PANEL_PRIMARY_BTN_CLASS}
              style={{ fontFamily: 'CustomFont' }}
            >
              ذخیره
            </Button>
          </div>
        </Form>
        </Spin>
      </Card>
      )}

      <WorkManageEntityModal
        open={typeModal}
        onClose={() => setTypeModal(false)}
        title="نوع قرارداد"
        nameField="name"
        nameLabel="نام نوع"
        loadItems={refreshTypes}
        onCreate={(name) => contractTypesApi.create(name)}
        onUpdate={(tid, name) => contractTypesApi.update(tid, name)}
        onDelete={(tid) => contractTypesApi.delete(tid)}
        buildCreatePayload={(v) => v.name}
        buildUpdatePayload={(tid, name) => name}
      />
    </WorkFormPage>
  );
};

export default ContractForm;
